import { Camera, Mic, BarChart3, Image as ImageIcon, MessageCircle, Pencil, MoreVertical } from "lucide-react";
import { REQUEST_STATUS_DOT, ADMIN_QUOTE_STATUS, ATTENTION, isOverdue, waitedFor } from "../quoteUtils";

// Blinking dot — respects "reduce motion" (then it's a solid dot)
export function AttentionDot({ overdue }) {
  const color = overdue ? "bg-red-500" : "bg-amber-400";
  return (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
      <span className={`absolute inline-flex h-full w-full rounded-full ${color} opacity-75 motion-safe:animate-ping`} />
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${color}`} />
    </span>
  );
}

export function AttentionChip({ attention }) {
  if (!attention) return null;
  const overdue = isOverdue(attention);
  const meta = ATTENTION[attention.reason] || { short: "Needs attention" };
  return (
    <span className={`inline-flex items-center gap-1.5 mt-1.5 text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${overdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
      <AttentionDot overdue={overdue} />
      {meta.short} · {waitedFor(attention.since)}
    </span>
  );
}

export default function RequestRow({ r, onClick, actions }) {
  const a = r.attention;
  const overdue = isOverdue(a);
  const rowTone = a ? (overdue ? "bg-red-50/70 hover:bg-red-50" : "bg-amber-50/70 hover:bg-amber-50") : "hover:bg-slate-50/50";
  const accent = a ? (overdue ? "border-l-red-500" : "border-l-amber-400") : "border-l-transparent";

  return (
    <tr onClick={onClick} className={`border-b border-gray-50 cursor-pointer ${rowTone}`}>
      <td className={`py-4 px-4 border-l-4 ${accent}`}>
        <div className="font-semibold text-teal-600">{r.requestId}</div>
        <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5 flex-wrap">
          <span className={`w-2 h-2 rounded-full ${REQUEST_STATUS_DOT[r.status] || "bg-slate-400"}`} />{r.status}
          {r.quoteStatus && r.quoteStatus !== "pending" && (
            <span className="text-slate-400">· quote {(ADMIN_QUOTE_STATUS[r.quoteStatus]?.label || r.quoteStatus).toLowerCase()}</span>
          )}
        </div>
        {r.quotedByName && <div className="text-[11px] text-slate-400 mt-0.5">Quoted by {r.quotedByName}</div>}
        <AttentionChip attention={a} />
      </td>
      <td className="py-4 px-4"><div className="font-semibold text-slate-800">{r.customerName}</div><div className="text-xs text-slate-400">{r.customerContact}</div></td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded bg-slate-100 text-slate-400"><Camera size={14} /></span>
          <div>
            <div className="font-semibold text-slate-700">{r.rego || (r.vin ? r.vin.slice(0, 7) : "—")}</div>
            <div className="text-xs text-slate-400">{r.vin ? `VIN: ${r.vin}` : (r.rego ? `Rego: ${r.rego}` : "")}</div>
          </div>
        </div>
      </td>
      <td className="py-4 px-4"><div className="font-semibold text-slate-800">{r.partRequired || r.notes || "—"}</div><div className="text-xs text-slate-400">{r.partNumber}</div></td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2 text-slate-400">
          {r.hasVoice && <Mic size={16} />}{r.hasVoice && <BarChart3 size={16} />}{r.hasPhoto && <ImageIcon size={16} />}
          {!r.hasVoice && !r.hasPhoto && <span className="text-xs">—</span>}
        </div>
      </td>
      <td className="py-4 px-4">
        <div className={`flex items-center gap-1 ${r.lastComment?.role === "user" ? "text-red-600 font-semibold" : "text-slate-500"}`}>
          <MessageCircle size={16} /> {r.commentCount || 0}
        </div>
        {r.lastComment?.role === "user" ? (
          <div className="text-[11px] text-red-600 mt-0.5">Customer wrote {waitedFor(r.lastComment.at)} ago</div>
        ) : r.lastStaffReply ? (
          <div className="text-[11px] text-slate-400 mt-0.5">Replied by {r.lastStaffReply.name}</div>
        ) : null}
      </td>
      <td className="py-4 px-4"><div className="flex items-center gap-1 text-slate-500 max-w-[160px] truncate">{r.notes} {r.notes && <Pencil size={12} />}</div></td>
      <td className="py-4 px-4">{actions || <MoreVertical size={16} className="text-slate-400" />}</td>
    </tr>
  );
}
